import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Loader2, Banknote, Users, CalendarClock, CheckCircle2, Clock,
  ShieldCheck, ChevronDown, ChevronUp, RefreshCw,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FREQUENCIES = [
  { id: 'monthly', label: 'Monthly (12/yr)' },
  { id: 'semimonthly', label: 'Twice monthly (24/yr)' },
  { id: 'biweekly', label: 'Every 2 weeks (26/yr)' },
  { id: 'weekly', label: 'Weekly (52/yr)' },
];

const PayrollGivingView = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [remitting, setRemitting] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/payroll/admin/summary`, { withCredentials: true });
      setSummary(res.data);
    } catch (err) {
      console.error('Failed to load payroll summary:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setFrequency = async (frequency) => {
    try {
      await axios.put(`${BACKEND_URL}/api/payroll/admin/settings`, { frequency, enabled: true }, { withCredentials: true });
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not update the pay cadence');
    }
  };

  const remit = async (charity) => {
    setRemitting(charity.charity_ein);
    setError('');
    try {
      const res = await axios.post(`${BACKEND_URL}/api/payroll/admin/remittance`, {
        charity_ein: charity.charity_ein,
        return_origin: window.location.origin,
      }, { withCredentials: true });

      // The employer pays Every.org directly — we record it only when their webhook confirms
      window.open(res.data.donate_url, '_blank', 'noopener');
      const poll = setInterval(async () => {
        try {
          const check = await axios.get(`${BACKEND_URL}/api/payroll/admin/summary`, { withCredentials: true });
          const found = (check.data.remittances || []).find(r => r.remittance_id === res.data.remittance_id);
          if (found && found.payment_status === 'paid') {
            clearInterval(poll);
            setSummary(check.data);
          }
        } catch (e) { /* keep polling while the admin completes the transfer */ }
      }, 5000);
      setTimeout(() => clearInterval(poll), 300000);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not start the remittance');
    } finally {
      setRemitting(null);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 text-blue-500 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="payroll-giving-view">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payroll Giving</h1>
          <p className="text-sm text-gray-500 mt-1">
            Employees pledge from their pay, you remit the pooled total by bank transfer — no card fees, 100% reaches the charity
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={summary?.frequency || 'monthly'} onChange={e => setFrequency(e.target.value)}
            data-testid="payroll-frequency-select"
            className="px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white">
            {FREQUENCIES.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
          </select>
          <button onClick={load} className="p-2 bg-white border border-gray-200 rounded-xl hover:bg-gray-50">
            <RefreshCw className="w-4 h-4 text-gray-500" />
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4 text-xs text-red-800" data-testid="payroll-error">{error}</div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { icon: Users, label: 'Participating employees', value: summary?.participants || 0, tone: 'from-blue-500 to-indigo-600' },
          { icon: Banknote, label: 'Per pay run', value: `$${(summary?.per_period_total || 0).toLocaleString()}`, tone: 'from-emerald-500 to-teal-600' },
          { icon: CalendarClock, label: 'Annualised', value: `$${(summary?.annual_total || 0).toLocaleString()}`, tone: 'from-purple-500 to-indigo-600' },
          { icon: CheckCircle2, label: 'Remitted to date', value: `$${(summary?.total_remitted || 0).toLocaleString()}`, tone: 'from-amber-500 to-orange-600' },
        ].map(card => (
          <div key={card.label} className={`rounded-xl p-5 bg-gradient-to-br ${card.tone} text-white`}
            data-testid={`payroll-stat-${card.label.split(' ')[0].toLowerCase()}`}>
            <card.icon className="w-5 h-5 mb-3 opacity-80" />
            <p className="text-2xl font-black">{card.value}</p>
            <p className="text-sm font-medium text-white/80">{card.label}</p>
          </div>
        ))}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 flex items-start gap-3">
        <ShieldCheck className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-blue-900 leading-relaxed">
          Withhold each pledge through your normal payroll run, then click <strong>Remit now</strong> to send the pooled
          total to the charity through Every.org by bank transfer. Altrunic never holds the money, and every employee
          gift is credited to their impact record once the transfer clears.
        </p>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider">This pay run, by charity</h3>
        </div>
        <div className="divide-y divide-gray-50">
          {(summary?.charities || []).map((c, i) => (
            <div key={c.charity_ein} data-testid={`payroll-charity-${i}`}>
              <div className="px-6 py-4 flex items-center justify-between gap-4">
                <div className="flex-1 cursor-pointer" onClick={() => setExpanded(expanded === c.charity_ein ? null : c.charity_ein)}>
                  <p className="text-sm font-bold text-gray-900">{c.charity_name}</p>
                  <p className="text-xs text-gray-500">
                    EIN {c.charity_ein} · {c.participants} employee{c.participants !== 1 ? 's' : ''} ·
                    ${(c.total_remitted || 0).toLocaleString()} remitted so far
                  </p>
                </div>
                <p className="text-lg font-black text-gray-900">${c.per_period_amount.toLocaleString()}</p>
                <button onClick={() => remit(c)} disabled={remitting === c.charity_ein}
                  data-testid={`remit-btn-${i}`}
                  className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl text-xs font-bold hover:from-emerald-700 hover:to-teal-700 disabled:opacity-50 flex items-center gap-1.5">
                  {remitting === c.charity_ein ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Banknote className="w-3.5 h-3.5" />}
                  Remit now
                </button>
                <button onClick={() => setExpanded(expanded === c.charity_ein ? null : c.charity_ein)}
                  className="text-gray-400" data-testid={`expand-payroll-${i}`}>
                  {expanded === c.charity_ein ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
              </div>
              {expanded === c.charity_ein && (
                <div className="px-6 pb-4 bg-gray-50">
                  <table className="w-full text-sm">
                    <tbody className="divide-y divide-gray-100">
                      {c.pledges.map(p => (
                        <tr key={p.pledge_id}>
                          <td className="py-2 text-gray-700">{p.employee_name || p.employee_email}</td>
                          <td className="py-2 text-right text-gray-500 text-xs">${(p.total_given || 0).toLocaleString()} to date</td>
                          <td className="py-2 text-right font-bold text-gray-900">${p.amount_per_period?.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
          {(summary?.charities || []).length === 0 && (
            <div className="px-6 py-12 text-center" data-testid="payroll-empty">
              <Banknote className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">No payroll pledges yet</p>
              <p className="text-xs text-gray-400 mt-1">Employees set these up from the Impact Portal under “Payroll Giving”</p>
            </div>
          )}
        </div>
      </div>

      {(summary?.remittances || []).length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider">Remittance history</h3>
          </div>
          <table className="w-full" data-testid="payroll-remittances-table">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase">Period</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase">Charity</th>
                <th className="text-center px-4 py-3 text-xs font-bold text-gray-500 uppercase">Employees</th>
                <th className="text-right px-4 py-3 text-xs font-bold text-gray-500 uppercase">Amount</th>
                <th className="text-center px-4 py-3 text-xs font-bold text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {summary.remittances.map((r, i) => (
                <tr key={r.remittance_id} className="hover:bg-gray-50" data-testid={`remittance-row-${i}`}>
                  <td className="px-4 py-3 text-sm text-gray-600">{r.period_label}</td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900">{r.charity_name}</td>
                  <td className="px-4 py-3 text-center text-sm text-gray-600">{r.participants}</td>
                  <td className="px-4 py-3 text-right text-sm font-bold text-gray-900">${r.amount?.toLocaleString()}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-bold ${
                      r.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                    }`}>
                      {r.payment_status === 'paid' ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                      {r.payment_status === 'paid' ? 'Sent' : 'Awaiting transfer'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PayrollGivingView;
